// Lecture d'un flux SSE reçu en réponse d'un `fetch` POST.
//
// Pourquoi pas `EventSource` : l'API native ne sait faire que des GET, et POST /api/analyze a un
// corps (`{ blobId }`). On lit donc le corps de la réponse à la main. Le format est simple —
// des blocs `event: <nom>` / `data: <json>` séparés par une ligne vide — mais un morceau réseau
// peut couper n'importe où, y compris au milieu d'une ligne : d'où un parseur incrémental, testé
// avec des découpages hostiles (test/analyzeStream.test.ts).

export interface EvenementSse {
  nom: string;
  /** Contenu des lignes `data:` du bloc, concaténées (le protocole SSE autorise plusieurs lignes). */
  donnees: string;
}

export class ParseurSse {
  private tampon = '';

  /** Ajoute un morceau de texte et rend les évènements devenus complets. */
  ajouter(morceau: string): EvenementSse[] {
    this.tampon += morceau.replace(/\r\n/g, '\n');
    const evenements: EvenementSse[] = [];
    let separation = this.tampon.indexOf('\n\n');
    while (separation !== -1) {
      const bloc = this.tampon.slice(0, separation);
      this.tampon = this.tampon.slice(separation + 2);
      separation = this.tampon.indexOf('\n\n');
      let nom = 'message';
      const donnees: string[] = [];
      for (const ligne of bloc.split('\n')) {
        if (ligne.startsWith(':') || ligne.trim() === '') continue; // commentaire / ligne vide
        if (ligne.startsWith('event:')) nom = ligne.slice(6).trim();
        else if (ligne.startsWith('data:')) donnees.push(ligne.slice(5).trimStart());
      }
      if (donnees.length > 0) evenements.push({ nom, donnees: donnees.join('\n') });
    }
    return evenements;
  }
}

/**
 * Lit un corps de réponse `fetch` et rend les évènements SSE au fur et à mesure.
 * Le flux est consommé jusqu'à sa fin : l'appelant n'a rien à fermer.
 */
export async function* lireFluxSse(corps: ReadableStream<Uint8Array>): AsyncGenerator<EvenementSse> {
  const lecteur = corps.getReader();
  const decodeur = new TextDecoder();
  const parseur = new ParseurSse();
  try {
    for (;;) {
      const { done, value } = await lecteur.read();
      if (done) break;
      for (const evenement of parseur.ajouter(decodeur.decode(value, { stream: true }))) {
        yield evenement;
      }
    }
  } finally {
    lecteur.releaseLock();
  }
}
